import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';

import { alcanca, ehAdmin, papeisDe, temPapel } from '../shared/papeis';
import { DecodedToken } from './useIsAdmin';

/**
 * Os papéis do usuário logado (at-81, ADR 0044).
 *
 * Fonte única do frontend: `useIsAdmin`, `useUserType` e `useIsTi` passaram a derivar daqui, de
 * modo que existe UM lugar que decide o que "ter um papel" significa. As assinaturas dos três
 * foram preservadas de propósito — trocá-las obrigaria a varrer 27 consumidores sem ganho.
 *
 * ⚠️ Lê o TOKEN, não o `state.auth.userType`: o Redux guarda o papel gravado no login e diverge
 * quando o token é renovado (foi o BUG-V49). O token é a única fonte que o backend também vê.
 */
export function usePapeis(): string[] {
  const token = useSelector((state: any) => state.auth.accessToken);

  if (!token) return [];

  try {
    const decoded = jwtDecode<DecodedToken>(token);
    return papeisDe(decoded);
  } catch {
    return [];
  }
}

/** `true` se o usuário tem QUALQUER um dos papéis informados. */
export function useTemPapel(...papeis: string[]): boolean {
  const doUsuario = usePapeis();
  return temPapel({ papeis: doUsuario }, ...papeis);
}

/** `true` se o usuário alcança a superfície (ADMIN sempre alcança). */
export function useAlcanca(permitidos: string[] | null | undefined): boolean {
  const doUsuario = usePapeis();
  return alcanca({ papeis: doUsuario }, permitidos);
}

export function useEhAdmin(): boolean {
  const doUsuario = usePapeis();
  return ehAdmin({ papeis: doUsuario });
}

/**
 * Tela inicial vinda do grupo de acesso de maior prioridade, quando existe.
 *
 * `null` = o usuário não está em grupo com tela definida, e o resolver cai na prioridade por
 * papel de sempre — que é o comportamento anterior aos grupos.
 */
export function useTelaInicialDoGrupo(): string | null {
  const token = useSelector((state: any) => state.auth.accessToken);

  if (!token) return null;

  try {
    const decoded = jwtDecode<DecodedToken>(token);
    return decoded.telaInicial ?? null;
  } catch {
    return null;
  }
}
